/**
 * HRTickets — live employee-inquiry tickets from /v1/hr/tickets with status
 * filters, a sensitive-first default sort, a detail drawer (message + extracted
 * fields), and the status lifecycle action (PATCH /v1/hr/tickets/{id}/status).
 * Clone of FinanceInvoices' list → drawer → action shape.
 *
 * Sensitivity is DB-generated from category (the safety boundary) — the UI only
 * renders it. P1 treatment: amber is the sensitivity signal and NOTHING else —
 * an amber dot + "SENSITIVE" wordmark, a 2px amber left bar on the row, and a
 * warm row tint while the ticket is unresolved. Statuses render as quiet
 * colored dots + labels (statusTicket dot/label tokens), never amber, so
 * within a row amber encodes exactly one thing.
 */
import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X, Check, ShieldAlert } from 'lucide-react'
import { SectionTitle, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { usePolledAsync, fetchTickets, updateTicketStatus } from '../../api'
import { useWriteAccess } from '../../../../lib/writeAccess'
import type { HRTicket } from '../../types'
import { apiErrorMessage } from '../../../../api/client'
import { toast } from 'sonner'
import {
  accent, confidence as confidenceScale, ink, neutral, overlay, sensitive,
  statusTicket, success,
} from '../../../../theme/tokens'

const TICKET_STATUSES = ['new', 'open', 'in_progress', 'resolved', 'closed'] as const

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ')
}

// Status = colored dot + label (P1). Unknown statuses fall back to the neutral dot.
function TicketStatus({ status }: { status: string }) {
  const cfg = statusTicket[status as keyof typeof statusTicket] ?? { dot: neutral.dot, label: ink.secondary }
  return (
    <span className="inline-flex items-center gap-2 text-12 font-medium capitalize whitespace-nowrap"
      style={{ color: cfg.label }}>
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: cfg.dot }} />
      {statusLabel(status)}
    </span>
  )
}

// Bare sensitivity dot — the row grid's leading slot (wordmark rides the subject line).
function SensitivityDot({ sensitivity }: { sensitivity: string }) {
  return (
    <span className="w-2 h-2 rounded-full flex-shrink-0"
      style={{ background: sensitivity === 'sensitive' ? sensitive.indicator : neutral.dot }} />
  )
}

// Sensitivity = amber dot + wordmark for 'sensitive'; quiet neutral dot for 'safe'.
function SensitivityMark({ sensitivity }: { sensitivity: string }) {
  if (sensitivity === 'sensitive') {
    return (
      <span className="inline-flex items-center gap-2 whitespace-nowrap">
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: sensitive.indicator }} />
        <span className="text-11 font-semibold uppercase tracking-overline" style={{ color: sensitive.wordmark }}>
          Sensitive
        </span>
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: neutral.dot }} />
      <span className="text-11 font-medium" style={{ color: ink.muted }}>Safe</span>
    </span>
  )
}

// A ticket is "unresolved" while a human still owes it action — drives the row tint.
function isUnresolved(status: string): boolean {
  return status !== 'resolved' && status !== 'closed'
}

// ── Sorting ─────────────────────────────────────────────────────────────────────

type SortMode = 'sensitive-first' | 'newest' | 'status'

const STATUS_ORDER: Record<string, number> = { new: 0, open: 1, in_progress: 2, resolved: 3, closed: 4 }

function sortTickets(list: HRTicket[], mode: SortMode): HRTicket[] {
  const newest = (a: HRTicket, b: HRTicket) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  const out = [...list]
  if (mode === 'newest') return out.sort(newest)
  if (mode === 'status') {
    return out.sort((a, b) =>
      (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99) || newest(a, b))
  }
  // Default: sensitive tier first, newest-first WITHIN each tier — a sensitive
  // ticket can never be buried by later safe mail.
  return out.sort((a, b) =>
    Number(b.sensitivity === 'sensitive') - Number(a.sensitivity === 'sensitive') || newest(a, b))
}

function fmtReceived(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

// Row-grid date slot is 56px — day + month only (the drawer shows the full stamp).
function fmtReceivedShort(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
}

// ── Detail drawer ───────────────────────────────────────────────────────────────

function TicketDrawer({ ticket, onClose, onSetStatus, busy }: {
  ticket: HRTicket
  onClose: () => void
  /** Absent for a role without records access: no status control. */
  onSetStatus?: (id: string, status: string) => void
  busy: boolean
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  // The lifecycle target tracks the ticket, resyncing after each update (the
  // drawer stays open across transitions, unlike the one-shot invoice approve).
  const [target, setTarget] = useState<string>(ticket.status)
  useEffect(() => { setTarget(ticket.status) }, [ticket.status])

  const fields = ticket.extractedFields ?? {}
  const fieldStr = (key: string): string => {
    const v = fields[key]
    return typeof v === 'string' ? v : ''
  }
  const keyDates = Array.isArray(fields.key_dates)
    ? (fields.key_dates as unknown[]).map(String).filter(Boolean)
    : []
  const confidence = typeof fields.confidence === 'number' ? (fields.confidence as number) : null

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="k-drawer"
        style={{ width: 'min(560px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div className="min-w-0">
            <div className="text-detail-title font-bold text-ink-primary truncate">{ticket.subject || '(no subject)'}</div>
            <div className="text-12 text-ink-muted">{ticket.senderEmail || 'Unknown sender'}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-control text-ink-muted hover:bg-surface-nav-hover transition-colors flex-shrink-0">
            <X size={16} />
          </button>
        </div>

        <div className="flex items-center gap-3 px-6 py-4 border-b border-edge-row flex-shrink-0 flex-wrap">
          <SensitivityMark sensitivity={ticket.sensitivity} />
          <TicketStatus status={ticket.status} />
          <span className="k-badge bg-surface-panel text-ink-secondary">
            {ticket.category.toUpperCase()}
          </span>
          {confidence !== null && (
            <span className="inline-flex items-center gap-2 text-12 text-ink-secondary ml-auto whitespace-nowrap">
              AI confidence
              {/* meter: sage ≥ 85 (the platform's safe-accept threshold), ochre below */}
              <span className="inline-block w-16 h-1 rounded-full overflow-hidden"
                style={{ background: confidenceScale.track }}>
                <span className="block h-full rounded-full"
                  style={{
                    width: `${Math.max(0, Math.min(100, confidence))}%`,
                    background: confidence >= 85 ? confidenceScale.high : confidenceScale.low,
                  }} />
              </span>
              <span className="font-semibold text-ink-primary">{confidence}</span>
            </span>
          )}
        </div>

        {ticket.sensitivity === 'sensitive' && (
          <div className="px-6 pt-4 flex-shrink-0">
            <div className="flex items-center gap-2 px-3 py-2 rounded-tile text-12"
              style={{
                background: sensitive['banner-bg'],
                border: `1px solid ${sensitive['banner-border']}`,
                color: sensitive['banner-text'],
              }}>
              <ShieldAlert size={12} className="flex-shrink-0" />
              Sensitive category — auto-flagged from the ticket category; handle with discretion.
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-4">
          <div>
            <div className="k-eyebrow uppercase mb-1">Message</div>
            {ticket.bodyPreview ? (
              <p className="text-13 text-ink-body leading-relaxed whitespace-pre-wrap">{ticket.bodyPreview}</p>
            ) : (
              <p className="text-12 text-ink-muted">No message body.</p>
            )}
          </div>

          {fieldStr('summary') && (
            <div>
              <div className="k-eyebrow uppercase mb-1">AI Summary</div>
              <p className="text-13 text-ink-body leading-relaxed">{fieldStr('summary')}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-13">
            <div><div className="text-11 text-ink-muted">Employee</div><div className="text-ink-primary">{fieldStr('employee_name') || '—'}</div></div>
            <div><div className="text-11 text-ink-muted">Request type</div><div className="text-ink-primary">{fieldStr('request_type') || '—'}</div></div>
            <div><div className="text-11 text-ink-muted">Key dates</div><div className="text-ink-primary">{keyDates.length > 0 ? keyDates.join(', ') : '—'}</div></div>
            <div><div className="text-11 text-ink-muted">Received</div><div className="text-ink-primary">{fmtReceived(ticket.createdAt)}</div></div>
          </div>
        </div>

        {onSetStatus && <div className="flex items-center gap-2 px-6 pt-5 pb-14 border-t border-edge-row flex-shrink-0">
          <select value={target} onChange={e => setTarget(e.target.value)}
            className="flex-1 h-10 px-3 text-13 bg-surface-control border border-edge-control rounded-control text-ink-body capitalize focus:outline-none focus:border-accent">
            {TICKET_STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
          </select>
          <button onClick={() => onSetStatus(ticket.id, target)} disabled={busy || target === ticket.status}
            className="k-btn k-btn--lg flex-1 inline-flex items-center justify-center gap-2 text-ink-on-accent disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: target === 'resolved' ? success.DEFAULT : accent.DEFAULT }}>
            <Check size={14} /> {busy ? 'Updating…' : target === 'resolved' ? 'Mark resolved' : 'Update status'}
          </button>
        </div>}
      </div>
    </div>,
    document.body
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

const FILTERS = ['all', ...TICKET_STATUSES] as const

export default function HRTickets() {
  const { data, loading, error } = usePolledAsync(fetchTickets, 10_000, [])
  const [tickets, setTickets] = useState<HRTicket[]>([])
  const [statusFilter, setStatusFilter] = useState<(typeof FILTERS)[number]>('all')
  const [sortMode, setSortMode] = useState<SortMode>('sensitive-first')
  const [detail, setDetail] = useState<HRTicket | null>(null)
  const [busy, setBusy] = useState(false)
  // Ticket status is a records-level HR write (lib/writeAccess; fail closed).
  const { records } = useWriteAccess()

  useEffect(() => { if (data) setTickets(data) }, [data])

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const filtered = sortTickets(
    tickets.filter(t => statusFilter === 'all' || t.status === statusFilter),
    sortMode,
  )
  const sensitiveCount = tickets.filter(t => t.sensitivity === 'sensitive').length

  async function setStatus(id: string, status: string) {
    setBusy(true)
    try {
      const updated = await updateTicketStatus(id, status)
      setTickets(prev => prev.map(t => t.id === id ? updated : t))
      setDetail(d => (d && d.id === id ? updated : d))
      toast.success(`Ticket marked ${statusLabel(status)}`)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Update failed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle
        title="Tickets"
        subtitle={`${filtered.length} ticket${filtered.length !== 1 ? 's' : ''} · ${sensitiveCount} sensitive`}
      />

      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-2 rounded-full text-12 font-medium transition-colors capitalize ${statusFilter === s ? 'bg-ink-primary text-ink-on-accent border border-ink-primary' : 'bg-surface-control text-ink-secondary hover:bg-surface-nav-hover border border-edge-control hover:border-edge-control-hover'}`}>
            {s === 'all' ? 'All' : statusLabel(s)}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-11 text-ink-muted">Sort</span>
          <select value={sortMode} onChange={e => setSortMode(e.target.value as SortMode)}
            className="h-8 px-2 text-12 bg-surface-control border border-edge-control hover:border-edge-control-hover rounded-control text-ink-body focus:outline-none focus:border-accent transition-colors">
            <option value="sensitive-first">Sensitive first</option>
            <option value="newest">Newest</option>
            <option value="status">By status</option>
          </select>
        </div>
      </div>

      <div className="k-card">
        {filtered.length === 0 ? (
          <EmptyState
            message="No tickets here"
            hint={statusFilter === 'all'
              ? 'New employee emails to hr@ will appear as tickets.'
              : 'Nothing matches this status filter.'}
            actionLabel={statusFilter !== 'all' ? 'Show all' : undefined}
            onAction={() => setStatusFilter('all')}
          />
        ) : (
          /* Mockup row grid: [dot][category 120][subject+preview flex][status 96][date 56] */
          <div>
            {filtered.map(t => {
              const sens = t.sensitivity === 'sensitive'
              const done = !isUnresolved(t.status)
              return (
                <div key={t.id} onClick={() => setDetail(t)}
                  className={`flex items-center gap-4 px-5 ${t.bodyPreview ? 'py-3' : 'py-2'} border-b border-edge-row-faint last:border-b-0 cursor-pointer hover:bg-surface-row-hover transition-colors ${done ? 'opacity-[0.62]' : ''} ${sens ? 'ticket-row-sensitive' : ''} ${sens && !done ? 'ticket-row-sensitive--open' : ''}`}>
                  <SensitivityDot sensitivity={t.sensitivity} />
                  <span className="w-[120px] flex-shrink-0 text-12 text-ink-secondary capitalize truncate">{t.category}</span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="text-13 font-medium text-ink-primary truncate">{t.subject || '(no subject)'}</span>
                      {sens && (
                        <span className="text-11 font-semibold uppercase tracking-overline flex-shrink-0"
                          style={{ color: sensitive.wordmark }}>
                          Sensitive
                        </span>
                      )}
                    </span>
                    {t.bodyPreview && (
                      <span className="block text-12 text-ink-muted truncate">{t.bodyPreview}</span>
                    )}
                  </span>
                  <span className="w-24 flex-shrink-0"><TicketStatus status={t.status} /></span>
                  <span className="w-14 flex-shrink-0 text-right text-12 text-ink-muted whitespace-nowrap">{fmtReceivedShort(t.createdAt)}</span>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {detail && (
        <TicketDrawer
          ticket={detail}
          onClose={() => setDetail(null)}
          onSetStatus={records ? setStatus : undefined}
          busy={busy}
        />
      )}
    </div>
  )
}
